(() => {
  const SEARCH_PATH = '/search';
  const DISPLAY_QUERY_PARAM = 'mh_query';
  const TECHNICAL_QUERY_PATTERN =
    /(^|\s)(?:tag|product_type|title|vendor|id):|(\s|\()(?:AND|OR|NOT)(\s|\))/i;

  const CATEGORY_TYPES = new Map([
    ['bileklik', 'bracelet'],
    ['bileklikler', 'bracelet'],
    ['bilekligi', 'bracelet'],
    ['bilekliği', 'bracelet'],
    ['kolye', 'necklace'],
    ['kolyeler', 'necklace'],
    ['kolyesi', 'necklace'],
    ['yuzuk', 'ring'],
    ['yüzük', 'ring'],
    ['yuzukler', 'ring'],
    ['yüzükler', 'ring'],
    ['yuzugu', 'ring'],
    ['yüzüğü', 'ring'],
    ['tesbih', 'prayer-beads'],
    ['tesbihler', 'prayer-beads'],
    ['tespih', 'prayer-beads'],
    ['tespihler', 'prayer-beads'],
    ['kupe', 'earring'],
    ['küpe', 'earring'],
    ['kupeler', 'earring'],
    ['küpeler', 'earring'],
    ['bros', 'brooch'],
    ['broş', 'brooch'],
  ]);

  const TAG_PHRASES = new Map([
    ['lapis lazuli', '(tag:Lapis OR tag:"Lapis Lazuli")'],
    ['kaplan gözü', 'tag:"Kaplan Gözü"'],
    ['kaplan gozu', 'tag:"Kaplan Gözü"'],
    ['pembe kuvars', 'tag:"Pembe Kuvars"'],
    ['şeffaf kuvars', 'tag:"Şeffaf Kuvars"'],
    ['seffaf kuvars', 'tag:"Şeffaf Kuvars"'],
    ['güneş taşı', 'tag:"Güneş Taşı"'],
    ['gunes tasi', 'tag:"Güneş Taşı"'],
    ['ay taşı', 'tag:"Ay Taşı"'],
    ['ay tasi', 'tag:"Ay Taşı"'],
    ['göz akik', 'tag:"Göz Akik"'],
    ['goz akik', 'tag:"Göz Akik"'],
    ['mavi akik', 'tag:"Mavi Akik"'],
    ['ametist', 'tag:Ametist'],
    ['akik', 'tag:Akik'],
    ['amazonit', 'tag:Amazonit'],
    ['akuamarin', 'tag:Akuamarin'],
    ['anyolit', 'tag:Anyolit'],
    ['apetit', 'tag:Apetit'],
    ['aventurin', 'tag:Aventurin'],
    ['florit', 'tag:Florit'],
    ['inci', 'tag:İnci'],
    ['kalsedon', 'tag:Kalsedon'],
    ['labradorit', 'tag:Labradorit'],
    ['lal', '(tag:Lal OR tag:"Lal (Garnet)")'],
    ['lapis', '(tag:Lapis OR tag:"Lapis Lazuli")'],
    ['obsidyen', 'tag:Obsidyen'],
    ['oniks', 'tag:Oniks'],
    ['sedef', 'tag:Sedef'],
    ['selenit', 'tag:Selenit'],
    ['sitrin', 'tag:Sitrin'],
    ['sodalit', 'tag:Sodalit'],
    ['terahertz', 'tag:Terahertz'],
    ['turkuaz', 'tag:Turkuaz'],
    ['yeşim', 'tag:Yeşim'],
    ['yesim', 'tag:Yeşim'],
  ]);

  const FEMALE_TERMS = new Set(['kadin', 'kadın', 'bayan']);
  const MALE_TERMS = new Set(['erkek']);

  const cleanToken = (token) => token.replace(/^[^\p{L}\p{N}]+|[^\p{L}\p{N}]+$/gu, '');
  const foldToken = (token) => cleanToken(token).toLocaleLowerCase('tr-TR');
  const unique = (items) => [...new Set(items)];

  const quoteSearchTerm = (term) => {
    const escaped = term.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
    return /\s|:/.test(escaped) ? `"${escaped}"` : escaped;
  };

  const categoryClause = (category) => {
    switch (category) {
      case 'bracelet':
        return '(product_type:Bileklik OR product_type:"Erkek Bileklik")';
      case 'necklace':
        return 'product_type:Kolye';
      case 'ring':
        return 'product_type:Yüzük';
      case 'prayer-beads':
        return '(product_type:Tesbih OR product_type:"Erkek Tesbih")';
      case 'earring':
        return 'product_type:Küpe';
      case 'brooch':
        return 'product_type:Broş';
      default:
        return '';
    }
  };

  const normalizeSearchQuery = (query) => {
    const original = String(query ?? '')
      .trim()
      .replace(/\s+/g, ' ');

    if (!original || TECHNICAL_QUERY_PATTERN.test(original)) return original;

    const remainingTerms = [];
    const tagClauses = [];
    let category = '';
    let genderFilter = '';
    const rawTokens = original.split(' ');
    const foldedTokens = rawTokens.map(foldToken);

    for (let index = 0; index < rawTokens.length; index += 1) {
      const rawToken = rawTokens[index];
      const foldedToken = foldedTokens[index];
      if (!foldedToken) continue;

      if (FEMALE_TERMS.has(foldedToken)) {
        genderFilter = 'NOT tag:Erkek';
        continue;
      }

      if (MALE_TERMS.has(foldedToken)) {
        genderFilter = 'tag:Erkek';
        continue;
      }

      const categoryType = CATEGORY_TYPES.get(foldedToken);
      if (categoryType) {
        category = categoryType;
        continue;
      }

      let matchedTag = false;
      for (const phraseLength of [2, 1]) {
        const phrase = foldedTokens.slice(index, index + phraseLength).join(' ');
        const tagClause = TAG_PHRASES.get(phrase);
        if (!tagClause) continue;

        tagClauses.push(tagClause);
        index += phraseLength - 1;
        matchedTag = true;
        break;
      }
      if (matchedTag) continue;

      const cleanedToken = cleanToken(rawToken);
      if (cleanedToken) remainingTerms.push(quoteSearchTerm(cleanedToken));
    }

    const clauses = [
      ...unique(remainingTerms),
      ...unique(tagClauses),
      ...(category ? [categoryClause(category)] : []),
      ...(genderFilter ? [genderFilter] : []),
    ];

    if (clauses.length < 2 && clauses[0] === original) return original;
    return clauses.join(' AND ');
  };

  const isSearchUrl = (url) => {
    const path = url.pathname.replace(/\/+$/, '') || '/';
    return path === SEARCH_PATH || path.endsWith(SEARCH_PATH);
  };

  const buildSearchUrl = (form, originalQuery) => {
    const action = form.getAttribute('action') || SEARCH_PATH;
    const url = new URL(action, window.location.origin);
    const formData = new FormData(form);

    formData.forEach((value, key) => {
      if (key !== 'q' && typeof value === 'string') url.searchParams.set(key, value);
    });

    const visibleInput = form.querySelector('input[type="search"]');
    const technicalInput = form.querySelector('[data-mh-technical-search-query]');
    const displayValue = visibleInput?.dataset.mhDisplayValue?.trim();
    const rawValue = visibleInput?.dataset.mhRawValue || technicalInput?.value || '';
    const preserveTechnicalQuery =
      visibleInput instanceof HTMLInputElement &&
      technicalInput instanceof HTMLInputElement &&
      originalQuery.trim() === displayValue &&
      TECHNICAL_QUERY_PATTERN.test(rawValue);
    const normalizedQuery = preserveTechnicalQuery ? rawValue : normalizeSearchQuery(originalQuery);
    url.searchParams.set('q', normalizedQuery);
    url.searchParams.set('type', 'product');
    url.searchParams.delete('page');

    if (normalizedQuery !== originalQuery.trim()) {
      url.searchParams.set(DISPLAY_QUERY_PARAM, originalQuery.trim());
    } else {
      url.searchParams.delete(DISPLAY_QUERY_PARAM);
    }

    return url;
  };

  const restoreDisplayQuery = () => {
    if (!isSearchUrl(new URL(window.location.href))) return;

    const url = new URL(window.location.href);
    const displayQuery = url.searchParams.get(DISPLAY_QUERY_PARAM)?.trim();
    if (!displayQuery) return;

    document.querySelectorAll('form input[type="search"]').forEach((input) => {
      if (!(input instanceof HTMLInputElement)) return;
      input.value = displayQuery;
      input.dataset.mhDisplayValue = displayQuery;
      input.dataset.mhRawValue = url.searchParams.get('q') ?? '';
    });

    const noResultsText = document.querySelector('.search-results__no-results p');
    if (noResultsText) {
      noResultsText.textContent = `"${displayQuery}" aramasıyla eşleşen ürün bulunamadı. Yazımı kontrol edebilir veya daha kısa bir arama deneyebilirsiniz.`;
    }
  };

  const normalizeDirectSearchUrl = () => {
    const url = new URL(window.location.href);
    if (!isSearchUrl(url)) return false;

    const query = url.searchParams.get('q')?.trim() ?? '';
    if (!query || url.searchParams.has(DISPLAY_QUERY_PARAM)) return false;

    const normalizedQuery = normalizeSearchQuery(query);
    if (!normalizedQuery || normalizedQuery === query) return false;

    url.searchParams.set('q', normalizedQuery);
    url.searchParams.set(DISPLAY_QUERY_PARAM, query);
    url.searchParams.set('type', 'product');
    url.searchParams.delete('page');
    window.location.replace(url.toString());
    return true;
  };

  window.mhNormalizeSearchQuery = normalizeSearchQuery;

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement) || form.method.toLowerCase() !== 'get') return;

      const actionUrl = new URL(form.getAttribute('action') || window.location.href, window.location.origin);
      if (!isSearchUrl(actionUrl)) return;

      const visibleInput = form.querySelector('input[type="search"]');
      const namedInput = form.querySelector('input[name="q"]');
      const queryInput = visibleInput instanceof HTMLInputElement ? visibleInput : namedInput;
      if (!(queryInput instanceof HTMLInputElement)) return;

      const originalQuery = queryInput.value.trim();
      if (!originalQuery) return;

      event.preventDefault();
      window.location.assign(buildSearchUrl(form, originalQuery).toString());
    },
    true
  );

  if (!normalizeDirectSearchUrl()) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', restoreDisplayQuery, { once: true });
    } else {
      restoreDisplayQuery();
    }
  }
})();
